import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import bgImage from "../assets/bg.jpg";
import logo from "../assets/logo.svg";
import { toast } from "react-toastify";
import { msalInstance, initializeMsal } from '../utils/msalInstance';
import MicrosoftLogo from "../assets/Microsoft_logo.svg";
import Loader from "../components/loader";
import serverURL from "../config";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMicrosoft, setLoadingMicrosoft] = useState(false);
  const [msalReady, setMsalReady] = useState(false);
  const redirectProcessedRef = useRef(false);
  const isMountedRef = useRef(true);

  // Initialize MSAL and handle redirects
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    isMountedRef.current = true;

    const initAndHandleRedirect = async () => {
      try {
        // Initialize MSAL
        await initializeMsal();
        
        // Only process redirect once
        if (!redirectProcessedRef.current) {
          redirectProcessedRef.current = true;
          
          // Check if we're returning from Microsoft login
          const redirectResponse = await msalInstance.handleRedirectPromise();
          
          if (redirectResponse !== null) {
            // We have a redirect response, show loading and process it
            if (isMountedRef.current) {
              setLoading(true);
              setLoadingMicrosoft(true);
            }
            
            await handleRedirectResponse(redirectResponse);
            return; // Exit early, navigation will happen in handleRedirectResponse
          }
        }
        
        // No redirect response, just set ready state
        if (isMountedRef.current) {
          setMsalReady(true);
          setLoading(false);
          setLoadingMicrosoft(false);
        }
      } catch (error) {
        console.error("MSAL initialization failed:", error);
        if (isMountedRef.current) {
          setMsalReady(true);
          setLoading(false);
          setLoadingMicrosoft(false);
          toast.error("Microsoft login service unavailable");
        }
      }
    };

    initAndHandleRedirect();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleRedirectResponse = async (response) => {
    try {
      if (!isMountedRef.current) return;

      const account = response.account;
      const microsoftId = account.localAccountId || account.homeAccountId.split('.')[0];
      const email = account.username;
      const name = account.name || email.split('@')[0];

      console.log("Processing Microsoft login for:", email);

      // Send to backend
      const backendResponse = await axios.post(
        `${serverURL}/auth/login/microsoft`,
        { microsoftId, email, name },
        { timeout: 10000 } // 10 second timeout
      );

      const { token, user } = backendResponse.data;
      
      // If backend returned a user object, save it for role checks
      if (user) {
        try { localStorage.setItem('user', JSON.stringify(user)); } catch (e) { }
      }

      // If backend returned a token, save it as well
      if (token) {
        localStorage.setItem("token", token);
      }

      // Show success message
      toast.success("Successfully logged in with Microsoft!");
      
      // Navigate within SPA so network tab isn't cleared
      setTimeout(() => {
        navigate("/dashboard");
      }, 500);
      
    } catch (error) {
      console.error("Error processing Microsoft redirect:", error);
      
      if (isMountedRef.current) {
        const errorMsg = error.response?.data?.message || 
                        error.message || 
                        "Microsoft login failed. Please try again.";
        toast.error(errorMsg);
        
        // Reset loading states so user can try again
        setLoading(false);
        setLoadingMicrosoft(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Basic validation
    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${serverURL}/auth/login`, formData);
      const { token, user } = response.data;

      if (!token) {
        throw new Error("No token received from server");
      }

      // Store token (if provided) and user
      localStorage.setItem("token", token);
      if (user) {
        try { localStorage.setItem('user', JSON.stringify(user)); } catch(e) {}
      }
      toast.success("Successfully logged in!");
      
      setTimeout(() => {
        if (user && user.role === "admin") {
          navigate("/dashboard");
        }else {
          navigate("/not-authorized");
        }
      }, 1000);
    } catch (error) {
      console.error("Login error:", error);
      const errorMsg = error.response?.data?.message || "Login failed. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!msalReady) {
      toast.error("Authentication service is still loading. Please wait...");
      return;
    }

    if (loading || loadingMicrosoft) {
      return; // Prevent double-click
    }

    setLoading(true);
    setLoadingMicrosoft(true);

    try {
      console.log("Initiating Microsoft login redirect...");

      // Trigger redirect to Microsoft login
      await msalInstance.loginRedirect({
        scopes: ['User.Read'],
        prompt: 'select_account',
      });
      
      // Code after this won't execute as page will redirect
    } catch (error) {
      console.error("Microsoft login redirect error:", error);
      
      if (isMountedRef.current) {
        setLoadingMicrosoft(false);
        setLoading(false);
        
        const errorMsg = error.errorMessage || "Microsoft login failed. Please try again.";
        toast.error(errorMsg);
      }
    }
  };
  return (
    <div>
      {loading && <Loader />}
      <div className="relative h-screen">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="relative h-screen flex items-center justify-center">
          <div className="bg-white p-4 px-8 md:p-8 rounded-lg shadow-lg w-4/5 md:w-3/5 lg:w-2/6">
            <div className="flex justify-center mb-6">
              <img src={logo} alt="vThink Logo" className="h-10 md:h-20 w-auto" />
            </div>
            <h2 className="md:text-xl font-semibold mb-3 text-center">Admin Login</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email*"
                  required
                  className="w-full p-2 md:p-3 rounded text-black placeholder-black"
                  style={{ backgroundColor: '#d3d3d3' }}
                />
              </div>
              <div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password*"
                  required
                  className="w-full p-2 md:p-3 rounded text-black placeholder-black"
                  style={{ backgroundColor: '#d3d3d3' }}
                />
              </div>
              {error && <div className="text-red-500 text-sm text-center">{error}</div>}
              <button
                type="submit"
                className="text-sm md:text-lg w-full text-white p-2 md:p-3 rounded transition-colors"
                style={{ backgroundColor: '#4c4c4c' }}
              >
                LOGIN
              </button>
            </form>

            <div className="flex items-center my-2 md:my-3">
              <hr className="flex-grow border-gray-300" />
              <span className="text-sm md:text-lg mx-2 text-gray-500">OR</span>
              <hr className="flex-grow border-gray-300" />
            </div>

            <button
              type="button"
              onClick={handleMicrosoftLogin}
              className={`text-sm md:text-lg w-full text-white p-2 md:p-3 rounded transition-colors ${loadingMicrosoft || !msalReady ? 'bg-gray-500' : 'bg-red-500'}`}
              disabled={loadingMicrosoft || !msalReady}
            >
              {loadingMicrosoft ? 'Logging in...' : !msalReady ? 'Loading...' : (
               <div className='flex items-center justify-center'>
                            <div className='bg-white w-auto flex flex-wrap p-1 rounded mr-2'>
                                <img src={MicrosoftLogo} alt="Microsoft logo" className="inline-block w-5 h-6 md:w-6 md:h-6" />
                            </div>
                        LOGIN WITH MICROSOFT
                        </div>
              )}
            </button>

            {/* <p className="text-center text-gray-600 mt-3">
              Doesn't have an account yet?{" "}
              <a href="/register" className="text-blue-600 hover:text-blue-800">
                Sign Up
              </a>
            </p> */}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Login;